import type { CustomerCardDto, DiscountDto } from '@deli/contracts';
import { useState } from 'react';
import { Link } from 'react-router';
import { useDiscounts } from '../api/pricing.js';
import { Can } from '../auth/session.js';
import { QueryState } from '../components/QueryState.js';
import { t } from '../i18n/uk.js';
import { DeleteWithReason, DiscountDialog, useDeleteDiscountAction } from './PricingDialogs.js';
import { formatDiscount, formatPeriod, stateBadge, targetLabel } from './PricingParts.js';

const tp = t.pricing;

/** The partner discount is the active customer-wide percentage (spec §3 `personal_discount`). */
const partnerDiscount = (items: DiscountDto[]) =>
  items.find(
    (d) => d.state === 'active' && !d.productId && !d.categoryId && d.mode === 'percent_off',
  );

/**
 * CUS-10: the customer's discounts on the card — the partner % for B2B first. Everyone with the
 * card sees them; `pricing.edit` may add, change and remove them here.
 */
export function CustomerPricing({ card }: { card: CustomerCardDto }) {
  const discounts = useDiscounts({ customerId: card.id });
  const [editing, setEditing] = useState<DiscountDto | 'new' | null>(null);
  const remove = useDeleteDiscountAction();
  const customer = { id: card.id, fullName: card.fullName, type: card.type, status: card.status };
  return (
    <div className="card">
      <h2>{tp.cardTitle}</h2>
      {card.status === 'blocked' ? (
        <p className="bad-text hint">{tp.blockedNote}</p>
      ) : (
        card.type === 'b2b' &&
        card.status !== 'active' && <p className="muted hint">{tp.pendingNote}</p>
      )}
      <QueryState query={discounts}>
        {(page) => {
          const partner = partnerDiscount(page.items);
          return (
            <>
              {card.type === 'b2b' && (
                <p>
                  {tp.partner}:{' '}
                  <strong>
                    {partner ? formatDiscount('percent_off', partner.value) : tp.partnerNone}
                  </strong>
                  <span className="muted"> · {tp.partnerHint}</span>
                </p>
              )}
              {page.items.length === 0 ? (
                <p className="muted">{tp.noDiscounts}</p>
              ) : (
                <table className="table">
                  <tbody>
                    {page.items.map((d) => (
                      <tr key={d.id}>
                        <td>{targetLabel(d)}</td>
                        <td className="num">{formatDiscount(d.mode, d.value)}</td>
                        <td>{formatPeriod(d.validFrom, d.validTo)}</td>
                        <td>{stateBadge(d.state)}</td>
                        <td className="muted">{d.reason}</td>
                        <Can permission="pricing.edit">
                          <td className="actions">
                            <button type="button" onClick={() => setEditing(d)}>
                              {t.common.edit}
                            </button>
                            <DeleteWithReason
                              label={formatDiscount(d.mode, d.value)}
                              onDelete={remove.run(d.id)}
                              pending={remove.pending}
                              error={remove.error}
                            />
                          </td>
                        </Can>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          );
        }}
      </QueryState>
      <Can permission="pricing.edit" fallback={<p className="muted hint">{tp.readOnly}</p>}>
        <div className="actions">
          <button type="button" onClick={() => setEditing('new')}>
            {tp.newDiscount}
          </button>
          <Link to="/pricing">{tp.title} →</Link>
        </div>
      </Can>
      {editing && (
        <DiscountDialog
          discount={editing === 'new' ? undefined : editing}
          customer={customer}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
